import { PIXI } from '@colonist/pixi';
import { logError } from './ui_utils/Utils.ts';
import { Sprites, getImageNameFromFileName, getImagesToPreload } from './ui_game/UIGameImageDictionary.ts';
import { ViewPopupAssetsLoading } from './ui_popup/ViewPopupAssetsLoading.ts';
import { UIImagePathController } from './UIEnums.ts';
import { AbortError } from '@colonist/types';
import { ArrayUtils, createDeferredPromise, EnumUtils } from '@colonist/utils';
import { CityImprovementTypes, PlayerColor, TileCornerBonusType, TileCornerBuildingType, TileEdgeDirection, TileEdgePortType, TileHexType } from '@colonist/game-states';
(function(UIGameSpriteHandler) {
    let loadingPopup;
    let spritesheetMap;
    let loadedAssets;
    const baseBundleName = 'game';
    let bundleManifest;
    async function initializeBundle() {
        if (bundleManifest != undefined) return;
        const assetsArray = [];
        for (const img of getImagesToPreload()){
            assetsArray.push({
                name: img.name,
                srcs: [
                    {
                        src: img.path
                    }
                ]
            });
        }
        bundleManifest = {
            bundles: [
                {
                    name: baseBundleName,
                    assets: assetsArray
                }
            ]
        };
        await PIXI.Assets.init({
            manifest: bundleManifest
        });
    }
    UIGameSpriteHandler.initializeBundle = initializeBundle;
    async function loadImages() {
        await UIGameSpriteHandler.initializeBundle();
        void PIXI.Assets.backgroundLoadBundle(baseBundleName);
    }
    UIGameSpriteHandler.loadImages = loadImages;
    async function continueAfterAssetsLoad(action = ()=>undefined, xAction = ()=>undefined) {
        if (loadedAssets != undefined) {
            action();
            return;
        }
        const promise = createDeferredPromise();
        const resolvePromiseAndRunAction = ()=>{
            action();
            promise.resolve();
        };
        if (loadingPopup != undefined && !loadingPopup.isRemoved()) {
            loadingPopup.addCallback(resolvePromiseAndRunAction);
            return promise;
        }
        loadingPopup = new ViewPopupAssetsLoading(resolvePromiseAndRunAction, ()=>{
            xAction();
            promise.reject(new AbortError());
        });
        loadingPopup.show();
        void startLoadingAssets();
        return promise;
    }
    UIGameSpriteHandler.continueAfterAssetsLoad = continueAfterAssetsLoad;
    async function startLoadingAssets() {
        await UIGameSpriteHandler.initializeBundle();
        loadedAssets = await PIXI.Assets.loadBundle(baseBundleName, (progress)=>{
            loadingPopup.updateLoadPercentage(progress * 100);
        });
        loadingPopup.finishedLoading();
    }
    UIGameSpriteHandler.startLoadingAssets = startLoadingAssets;
    function getProbabilityImage(prob) {
        const allowedDiceNumbers = [
            '2',
            '3',
            '4',
            '5',
            '6',
            '8',
            '9',
            '10',
            '11',
            '12'
        ];
        if (!allowedDiceNumbers.includes(prob.toString())) return undefined;
        const validDiceNumberStringTypes = [
            '2',
            '3',
            '4',
            '5',
            '6',
            '8',
            '9',
            '10',
            '11',
            '12'
        ];
        const probString = prob.toString();
        const textureKey = Sprites[`prob_${probString}`];
        return getTextureFromSpritesheet(textureKey);
    }
    UIGameSpriteHandler.getProbabilityImage = getProbabilityImage;
    function getHexImage(type) {
        switch(type){
            case TileHexType.Lumber:
                return getTextureFromSpritesheet(Sprites.tile_lumber_empty);
            case TileHexType.Brick:
                return getTextureFromSpritesheet(Sprites.tile_brick_empty);
            case TileHexType.Wool:
                return getTextureFromSpritesheet(Sprites.tile_wool_empty);
            case TileHexType.Desert:
                return getTextureFromSpritesheet(Sprites.tile_desert_empty);
            case TileHexType.Grain:
                return getTextureFromSpritesheet(Sprites.tile_grain_empty);
            case TileHexType.Ore:
                return getTextureFromSpritesheet(Sprites.tile_ore_empty);
            case TileHexType.Gold:
                return getTextureFromSpritesheet(Sprites.tile_gold_empty);
            case TileHexType.Fog:
                return getTextureFromSpritesheet(Sprites.tile_fog);
            case TileHexType.Sea:
                return getTextureFromSpritesheet(Sprites.tile_sea);
        }
    }
    UIGameSpriteHandler.getHexImage = getHexImage;
    function getHexForegroundImage(type) {
        switch(type){
            case TileHexType.Lumber:
                return getTextureFromSpritesheet(Sprites.tile_lumber_side);
            case TileHexType.Brick:
                return getTextureFromSpritesheet(Sprites.tile_brick_side);
            case TileHexType.Wool:
                return getTextureFromSpritesheet(Sprites.tile_wool_side);
            case TileHexType.Grain:
                return getTextureFromSpritesheet(Sprites.tile_grain_side);
            case TileHexType.Ore:
                return getTextureFromSpritesheet(Sprites.tile_ore_side);
            case TileHexType.Desert:
                return getTextureFromSpritesheet(Sprites.tile_desert_side);
            case TileHexType.Gold:
                return getTextureFromSpritesheet(Sprites.tile_gold_side);
            default:
                return undefined;
        }
    }
    UIGameSpriteHandler.getHexForegroundImage = getHexForegroundImage;
    function getCornerImage(corner) {
        if (corner.owner == undefined) return undefined;
        if (corner.buildingType == undefined) return undefined;
        switch(corner.buildingType){
            case TileCornerBuildingType.Settlement:
                return getSettlementForPlayer(corner.owner);
            case TileCornerBuildingType.City:
                return getCityForPlayer(corner.owner);
            case TileCornerBuildingType.DestroyedCity:
                return getDestroyedCityForPlayer(corner.owner);
            case TileCornerBuildingType.KnightActiveLevel1:
            case TileCornerBuildingType.KnightActiveLevel2:
            case TileCornerBuildingType.KnightActiveLevel3:
            case TileCornerBuildingType.KnightInactiveLevel1:
            case TileCornerBuildingType.KnightInactiveLevel2:
            case TileCornerBuildingType.KnightInactiveLevel3:
                return getKnightForPlayer(corner.owner, corner.buildingType);
        }
    }
    UIGameSpriteHandler.getCornerImage = getCornerImage;
    function getPortImage(portType) {
        switch(portType){
            case TileEdgePortType.Port:
                return getTextureFromSpritesheet(Sprites.port);
            case TileEdgePortType.PortLumber:
                return getTextureFromSpritesheet(Sprites.port_lumber);
            case TileEdgePortType.PortBrick:
                return getTextureFromSpritesheet(Sprites.port_brick);
            case TileEdgePortType.PortWool:
                return getTextureFromSpritesheet(Sprites.port_wool);
            case TileEdgePortType.PortGrain:
                return getTextureFromSpritesheet(Sprites.port_grain);
            case TileEdgePortType.PortOre:
                return getTextureFromSpritesheet(Sprites.port_ore);
        }
    }
    UIGameSpriteHandler.getPortImage = getPortImage;
    function getSettlementForPlayer(playerColor) {
        const colorId = mapPlayerColorToColorId(playerColor);
        const resourceId = Sprites[`settlement_${colorId}`];
        return getTextureFromSpritesheet(resourceId);
    }
    UIGameSpriteHandler.getSettlementForPlayer = getSettlementForPlayer;
    function getCityForPlayer(playerColor) {
        const colorId = mapPlayerColorToColorId(playerColor);
        const resourceId = Sprites[`city_${colorId}`];
        return getTextureFromSpritesheet(resourceId);
    }
    UIGameSpriteHandler.getCityForPlayer = getCityForPlayer;
    function getDestroyedCityForPlayer(playerColor) {
        const colorId = mapPlayerColorToColorId(playerColor);
        const resourceId = Sprites[`city_destroyed_${colorId}`];
        return getTextureFromSpritesheet(resourceId);
    }
    UIGameSpriteHandler.getDestroyedCityForPlayer = getDestroyedCityForPlayer;
    function getCityWallForPlayer(playerColor) {
        const colorId = mapPlayerColorToColorId(playerColor);
        const resourceId = Sprites[`city_wall_${colorId}`];
        return getTextureFromSpritesheet(resourceId);
    }
    UIGameSpriteHandler.getCityWallForPlayer = getCityWallForPlayer;
    function getRoadForPlayer(playerColor) {
        const colorId = mapPlayerColorToColorId(playerColor);
        const resourceId = Sprites[`road_${colorId}`];
        return getTextureFromSpritesheet(resourceId);
    }
    UIGameSpriteHandler.getRoadForPlayer = getRoadForPlayer;
    function getRoadHighlight() {
        const resourceId = Sprites[`road_highlight`];
        return getTextureFromSpritesheet(resourceId);
    }
    UIGameSpriteHandler.getRoadHighlight = getRoadHighlight;
    function getShipForPlayer(playerColor, edgeVariable) {
        const colorId = mapPlayerColorToColorId(playerColor);
        const resourceId = Sprites[`ship_${colorId}_${formatEdgeVariableAsString(edgeVariable)}`];
        return getTextureFromSpritesheet(resourceId);
    }
    UIGameSpriteHandler.getShipForPlayer = getShipForPlayer;
    function getShipHighlight(edgeVariable) {
        const resourceId = Sprites[`ship_highlight_${formatEdgeVariableAsString(edgeVariable)}`];
        return getTextureFromSpritesheet(resourceId);
    }
    UIGameSpriteHandler.getShipHighlight = getShipHighlight;
    function getKnightForPlayer(playerColor, knightType) {
        const knightStatus = formatKnightAsString(knightType);
        if (knightStatus == undefined) {
            logError('getKnightForPlayer', [
                playerColor
            ]);
            return getTextureFromSpritesheet(Sprites.knight_level1_inactive_green);
        }
        const colorId = mapPlayerColorToColorId(playerColor);
        const resourceId = Sprites[`knight_${knightStatus}_${colorId}`];
        return getTextureFromSpritesheet(resourceId);
    }
    UIGameSpriteHandler.getKnightForPlayer = getKnightForPlayer;
    function getMetropolisForCityImprovementType(cityImprovementType) {
        switch(cityImprovementType){
            case CityImprovementTypes.Politics:
                return getTextureFromSpritesheet(Sprites.metropolis_politics);
            case CityImprovementTypes.Trade:
                return getTextureFromSpritesheet(Sprites.metropolis_trade);
            case CityImprovementTypes.Science:
                return getTextureFromSpritesheet(Sprites.metropolis_science);
        }
    }
    UIGameSpriteHandler.getMetropolisForCityImprovementType = getMetropolisForCityImprovementType;
    function getMetropolisForCornerBonusType(cornerBonus) {
        switch(cornerBonus){
            case TileCornerBonusType.MetropolisPolitics:
                return getTextureFromSpritesheet(Sprites.metropolis_politics);
            case TileCornerBonusType.MetropolisTrade:
                return getTextureFromSpritesheet(Sprites.metropolis_trade);
            case TileCornerBonusType.MetropolisScience:
                return getTextureFromSpritesheet(Sprites.metropolis_science);
            default:
                logError('getMetropolisForCornerBonusType', []);
                return getTextureFromSpritesheet(Sprites.metropolis_science);
        }
    }
    UIGameSpriteHandler.getMetropolisForCornerBonusType = getMetropolisForCornerBonusType;
    function getButtonBadgeForPlayer(playerColor) {
        const colorId = mapPlayerColorToColorId(playerColor);
        const resourceId = Sprites[`button_badge_background_${colorId}`];
        return getTextureFromSpritesheet(resourceId);
    }
    UIGameSpriteHandler.getButtonBadgeForPlayer = getButtonBadgeForPlayer;
    function getTextureFromSpritesheet(textureKeyOrImageURL) {
        const textureKey = getAssetFileNameFromUrl(textureKeyOrImageURL);
        if (spritesheetMap == undefined) spritesheetMap = getSpriteToSpritesheetMap();
        const sheetName = spritesheetMap.get(textureKey);
        if (!sheetName) {
            // eslint-disable-next-line no-console
            console.error('getTextureFromSpritesheet, couldn\'t find sheet name for', [
                textureKey
            ]);
            return PIXI.Texture.WHITE;
        }
        if (loadedAssets == undefined) return PIXI.Texture.WHITE;
        const spritesheet = loadedAssets[sheetName];
        if (spritesheet == undefined) return PIXI.Texture.WHITE;
        return spritesheet.textures[textureKey];
    }
    UIGameSpriteHandler.getTextureFromSpritesheet = getTextureFromSpritesheet;
    function getTexture(textureKey) {
        if (loadedAssets == undefined) return PIXI.Texture.EMPTY;
        const texture = loadedAssets[textureKey];
        if (texture == undefined) return PIXI.Texture.EMPTY;
        return texture;
    }
    UIGameSpriteHandler.getTexture = getTexture;
    function getSpritesheet(sheetName) {
        if (loadedAssets == undefined) return undefined;
        return loadedAssets[sheetName];
    }
    UIGameSpriteHandler.getSpritesheet = getSpritesheet;
    function getTextureForLobbyImage(lobbyImage) {
        const fileName = getAssetFileNameFromUrl(UIImagePathController.getImgPath(lobbyImage));
        const texture = getImageNameFromFileName(fileName);
        if (!texture) return PIXI.Texture.EMPTY;
        if (loadedAssets == undefined) return PIXI.Texture.EMPTY;
        const pixiTexture = loadedAssets[texture];
        if (pixiTexture == undefined) return PIXI.Texture.EMPTY;
        return pixiTexture;
    }
    UIGameSpriteHandler.getTextureForLobbyImage = getTextureForLobbyImage;
    function mapPlayerColorToColorId(playerColor) {
        if (playerColor == PlayerColor.None) return 'white';
        return EnumUtils.getEnumKey(playerColor, PlayerColor).toLocaleLowerCase();
    }
    UIGameSpriteHandler.mapPlayerColorToColorId = mapPlayerColorToColorId;
    function getSpriteToSpritesheetMap() {
        const localSpritesheetMap = new Map();
        for(const key in loadedAssets){
            if (!key.startsWith('game_spritesheet_')) continue;
            const spriteSheet = loadedAssets[key];
            if (!spriteSheet) continue;
            Object.keys(spriteSheet.textures).forEach((texture)=>{
                localSpritesheetMap.set(texture, key);
            });
        }
        return localSpritesheetMap;
    }
    UIGameSpriteHandler.getSpriteToSpritesheetMap = getSpriteToSpritesheetMap;
})(UIGameSpriteHandler || (UIGameSpriteHandler = {}));
// handles name, name.svg, /path/name.svg, /path/name.svg?rev, /path/name.hash.svg
function getAssetFileNameFromUrl(url) {
    const assetFilename = ArrayUtils.last(url.split('/'));
    const name = assetFilename.split('.')[0];
    return name;
}
// eslint-disable-next-line colonist/camelcase-union-values -- sprite asset keys
function formatEdgeVariableAsString(edgeVariable) {
    switch(edgeVariable){
        case TileEdgeDirection.NorthWest:
            return 'north_west';
        case TileEdgeDirection.West:
            return 'west';
        case TileEdgeDirection.SouthWest:
            return 'south_west';
    }
}
/* eslint-enable colonist/camelcase-union-values */ function formatKnightAsString(knightType) {
    switch(knightType){
        case TileCornerBuildingType.KnightActiveLevel1:
            return 'level1_active';
        case TileCornerBuildingType.KnightActiveLevel2:
            return 'level2_active';
        case TileCornerBuildingType.KnightActiveLevel3:
            return 'level3_active';
        case TileCornerBuildingType.KnightInactiveLevel1:
            return 'level1_inactive';
        case TileCornerBuildingType.KnightInactiveLevel2:
            return 'level2_inactive';
        case TileCornerBuildingType.KnightInactiveLevel3:
            return 'level3_inactive';
        default:
            return undefined;
    }
}
export var UIGameSpriteHandler;
